/* reveal-testimonials — scattered "floating" testimonial cards around the big
   alife wordmark. Two behaviours, both progressive-enhancement only:
     1. Reveal: cards fade/scale/slide in once when scrolled into view
        (IntersectionObserver adds .is-visible; CSS does the rest).
     2. Parallax: on desktop the cards drift vertically at per-card speeds as the
        section moves through the viewport, giving the layered depth from Figma.
   The parallax transform lives on an inner wrapper (data-card) so it never fights
   the reveal transform on the card itself. Respects prefers-reduced-motion and
   collapses to a plain stack on mobile (parallax disabled below the desktop bp). */
if (!customElements.get('reveal-testimonials')) {
  class RevealTestimonials extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.cards = Array.from(this.querySelectorAll('[data-card]'));
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.desktop = window.matchMedia('(min-width: 990px)');

      this.reveal();

      if (!this.reduceMotion) {
        this.onScroll = this.requestTick.bind(this);
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll, { passive: true });
        this.update();
      }
    }

    disconnectedCallback() {
      if (this.onScroll) {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
      }
      if (this.io) this.io.disconnect();
    }

    /* One-shot entrance reveal. */
    reveal() {
      const show = (el) => el.closest('.rt__card').classList.add('is-visible');
      if (!('IntersectionObserver' in window)) {
        this.cards.forEach(show);
        return;
      }
      this.io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            show(entry.target);
            this.io.unobserve(entry.target);
          });
        },
        { threshold: 0.15, rootMargin: '0px 0px -8% 0px' }
      );
      this.cards.forEach((card) => this.io.observe(card));
    }

    requestTick() {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => this.update());
    }

    /* Vertical parallax driven by the section's progress through the viewport. */
    update() {
      this.ticking = false;

      if (!this.desktop.matches) {
        this.cards.forEach((card) => card.style.setProperty('--parallax', '0px'));
        return;
      }

      const rect = this.getBoundingClientRect();
      const viewport = window.innerHeight || document.documentElement.clientHeight;
      // -1 (section below the fold) → 0 (centred) → 1 (section above the fold)
      const centred = ((viewport - rect.top) / (viewport + rect.height) - 0.5) * 2;

      this.cards.forEach((card) => {
        const speed = parseFloat(card.getAttribute('data-speed')) || 0;
        // Amplitude (px) per unit of data-speed — higher = stronger parallax drift.
        const shift = (-centred * speed * 150).toFixed(1);
        card.style.setProperty('--parallax', `${shift}px`);
      });
    }
  }

  customElements.define('reveal-testimonials', RevealTestimonials);
}
